// Suggest a corrected email when the domain looks like a near-miss of a
// known popular provider (gmail / yahoo / outlook / etc.). Returns the full
// suggested email, or null if no suggestion applies.
//
// Conservative on purpose: never suggests when the user typed an exact
// known domain, and never suggests against domains that aren't in this
// dictionary — better to stay silent than to nudge someone toward the
// wrong address.

const POPULAR_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'outlook.com',
  'hotmail.com',
  'hotmail.co.uk',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'duck.com',
  'fastmail.com',
];

// Common TLD typos worth catching even when the rest of the domain is a
// recognized provider (gmail.con, gmail.co, yahoo.cm, etc.). We treat any
// non-exact TLD on a known-provider stem as a near-miss.
const POPULAR_STEMS = new Set(POPULAR_DOMAINS.map((d) => d.split('.')[0]));

// Damerau-Levenshtein with adjacent transposition. Counts a swap of two
// adjacent characters ("gmial" vs "gmail") as a single edit, which is the
// most common kind of email typo.
function damerauLevenshtein(a, b) {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;

  const d = Array.from({ length: al + 1 }, () => new Array(bl + 1).fill(0));
  for (let i = 0; i <= al; i++) d[i][0] = i;
  for (let j = 0; j <= bl; j++) d[0][j] = j;

  for (let i = 1; i <= al; i++) {
    for (let j = 1; j <= bl; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,        // deletion
        d[i][j - 1] + 1,        // insertion
        d[i - 1][j - 1] + cost, // substitution
      );
      if (
        i > 1 && j > 1 &&
        a.charCodeAt(i - 1) === b.charCodeAt(j - 2) &&
        a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
      ) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[al][bl];
}

export function suggestEmailCorrection(email) {
  if (typeof email !== 'string') return null;
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.indexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return null;

  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);

  // Exact match — nothing to suggest.
  if (POPULAR_DOMAINS.includes(domain)) return null;

  // Don't second-guess a domain without a dot — user is still typing.
  if (!domain.includes('.')) return null;

  // First pass: full-domain edit distance against the dictionary.
  let bestDomain = null;
  let bestDistance = Infinity;
  for (const known of POPULAR_DOMAINS) {
    const d = damerauLevenshtein(domain, known);
    if (d < bestDistance) {
      bestDistance = d;
      bestDomain = known;
    }
  }

  // Distance 1 is the sweet spot for Damerau-Levenshtein: single character
  // off, single insertion/deletion, or a single adjacent transposition.
  // Going higher catches more typos but also produces false positives on
  // legitimate uncommon domains.
  if (bestDomain && bestDistance === 1) {
    return `${local}@${bestDomain}`;
  }

  // Second pass: TLD-only typos on a recognized stem (gmail.con, gmail.co).
  // Catches the case where the dictionary distance is too far because the
  // user replaced the whole TLD with a wrong one.
  const firstDot = domain.indexOf('.');
  const stem = domain.slice(0, firstDot);
  if (POPULAR_STEMS.has(stem)) {
    const correctForStem = POPULAR_DOMAINS.find((d) => d.startsWith(`${stem}.`));
    if (correctForStem && correctForStem !== domain) {
      return `${local}@${correctForStem}`;
    }
  }

  return null;
}
